import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { doc, getDoc, collection, addDoc, updateDoc, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { auth, db, functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import Header from '@/components/layout/Header';
import { EventPageBackground } from '@/components/layout/EventPageBackground';
import { formatDiningDocumentTitle } from '@/components/layout/PageWithTitle';
import { formatDate, formatCurrency } from '@/lib/utils';
import { showSuccess, showError } from '@/lib/swal';
import { buildInvoicePaymentLink } from '@/lib/payment-links';
import { generateUnitInvoiceNumber } from '@/lib/invoice-utils';
import { optionalRsvpUserIdFields } from '@/lib/rsvp-submit-policy';
import { generateGuestRsvpToken, hashGuestRsvpToken, storeGuestRsvpToken } from '@/lib/guest-rsvp-token';
import { Calendar, MapPin, Lock, Minus, Plus } from 'lucide-react';
import { Event } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useRanks } from '@/hooks/useRanks';
import { VenueInfo } from '@/components/venues/VenueInfo';
import { UnitType } from '@/types';
import { getAvailablePrefixes, getRankAndShortName } from '@/lib/rank-utils';
import { formatRsvpFirstName, formatRsvpLastName } from '@/lib/format-rsvp-names';
import {
  validateMultiTenantTicketSelections,
  mergeMultiTenantTicketsIntoResponses,
  computeMultiTenantCartTotal,
  computeMultiTenantTotalQuantity,
} from '@/lib/rsvp-multi-tenant-ticket';
import {
  BOOKING_FEE_LABEL,
  getInvoiceCreditRecoveryPounds,
  shouldShowBookingFeeOnRsvp,
  isCreditRecoveryEnabledForEvent,
} from '@/lib/credit-booking-fee';
import { DEFAULT_CREDIT_BOOKING_FEE_PENCE, getCreditBookingFeePenceForEvent } from '@/lib/platform-booking-fee';
import { findFirstActiveRsvpDoc } from '@/lib/event-utils';
import { isMultiTenantEvent } from '@/lib/ticket-mode';

export default function MultiTenantRSVPPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const [searchParams] = useSearchParams();
  const ticketsParam = searchParams.get('tickets');
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const { ranks, loading: ranksLoading } = useRanks();
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [currentUnit, setCurrentUnit] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [event, setEvent] = useState<Event | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const emailRef = useRef(email);
  emailRef.current = email;
  const [loginPassword, setLoginPassword] = useState('');
  const [rank, setRank] = useState('');
  const [additionalRank, setAdditionalRank] = useState('');
  const [unitName, setUnitName] = useState('');
  const [emailHasAccount, setEmailHasAccount] = useState(false);
  const [lockPersonalDetailsFromAccount, setLockPersonalDetailsFromAccount] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [allergies, setAllergies] = useState('');

  const creditBookingFeePence = useMemo(() => getCreditBookingFeePenceForEvent(event), [event]);
  const currentUnitType = currentUnit ? unitTypes.find((ut) => ut.id === currentUnit.typeId) : undefined;
  const availablePrefixes = useMemo(() => getAvailablePrefixes(event, currentUnitType), [event, currentUnitType]);
  const hasConfiguredPrefixes = availablePrefixes.length > 0;
  const unitLabel = currentUnitType?.entityName || 'Unit/Organization';
  const requireAffiliationInfo = event?.requirePrefixRankUnitInfo ?? true;
  const isEventLocked = event?.eventStatus === 'closed';

  const availableTickets = useMemo(() => {
    const tickets = event?.tickets || [];
    if (ticketsParam) {
      const ids = ticketsParam.split(',').map((id) => id.trim());
      return tickets.filter((t) => ids.includes(t.id));
    }
    return tickets.filter((t) => !t.restricted);
  }, [event, ticketsParam]);

  useEffect(() => {
    if (!event) return;
    const segment = event.name?.trim() || 'RSVP';
    const next = formatDiningDocumentTitle(segment);
    if (typeof document !== 'undefined' && document.title !== next) {
      document.title = next;
    }
  }, [event]);

  useEffect(() => {
    const fetchUnit = async () => {
      if (!unitId) return;
      try {
        const unitDoc = await getDoc(doc(db, 'units', unitId));
        if (unitDoc.exists()) {
          setCurrentUnit({ id: unitDoc.id, ...unitDoc.data() });
        }
        const settingsDoc = await getDoc(doc(db, 'systemSettings', 'platform'));
        if (settingsDoc.exists()) {
          const settings = settingsDoc.data();
          setUnitTypes((settings.unitTypes as UnitType[]) || []);
        }
      } catch (error) {
        console.error('Error fetching unit:', error);
      }
    };
    fetchUnit();
  }, [unitId]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!unitId || !eventId) {
        showError('Invalid event link');
        setLoading(false);
        return;
      }

      try {
        const eventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
        if (!eventDoc.exists()) {
          showError('Event not found');
          setLoading(false);
          return;
        }

        const data = eventDoc.data();
        if (data.eventStatus === 'deleted') {
          showError('This event has been deleted and is no longer available for RSVPs.');
          setLoading(false);
          return;
        }

        const eventDate = data.date?.toDate() || new Date();
        const loadedEvent: Event = {
          id: eventDoc.id,
          unitId,
          name: data.name,
          ceremonyType: data.ceremonyType,
          date: eventDate,
          location: data.location,
          questionnaire: data.questionnaire || [],
          courses: data.courses || [],
          tickets: data.tickets || [],
          ticketMode: data.ticketMode === 'multiTenant' ? 'multiTenant' : 'mealChoice',
          maxCapacity: data.maxCapacity,
          currentAttendees: data.currentAttendees || 0,
          waitlistEnabled: data.waitlistEnabled || false,
          waitlist: data.waitlist || [],
          rsvpDeadline: data.rsvpDeadline?.toDate() || new Date(),
          paymentDeadline: data.paymentDeadline?.toDate() || (data.rsvpDeadline?.toDate() || eventDate),
          eventStatus: data.eventStatus,
          dresscode: data.dresscode,
          customInstructions: data.customInstructions,
          isPublic: data.isPublic ?? false,
          requirePrefixRankUnitInfo: data.requirePrefixRankUnitInfo ?? true,
          useCustomPrefixes: Boolean(data.useCustomPrefixes),
          customPrefixes: Array.isArray(data.customPrefixes)
            ? data.customPrefixes.filter((p: unknown) => typeof p === 'string' && p.trim() !== '')
            : [],
          logoUrl: data.logoUrl,
          bannerUrl: data.bannerUrl,
          backgroundImageUrl: data.backgroundImageUrl,
          backgroundColor: data.backgroundColor,
          createdBy: data.createdBy,
          createdAt: data.createdAt?.toDate() || new Date(),
          guestCoversCreditCost: data.guestCoversCreditCost ?? false,
          creditBookingFeePence:
            typeof data.creditBookingFeePence === 'number' ? data.creditBookingFeePence : undefined,
          creditCostRecovery:
            data.creditCostRecovery && typeof data.creditCostRecovery === 'object' && !Array.isArray(data.creditCostRecovery)
              ? {
                  enabled: Boolean((data.creditCostRecovery as { enabled?: boolean }).enabled),
                  amountPence:
                    typeof (data.creditCostRecovery as { amountPence?: unknown }).amountPence === 'number'
                      ? (data.creditCostRecovery as { amountPence: number }).amountPence
                      : 60,
                }
              : { enabled: false, amountPence: DEFAULT_CREDIT_BOOKING_FEE_PENCE },
        };

        // Only the multi-tenant page handles multi-tenant events; send meal-choice events back to the standard RSVP page.
        if (!isMultiTenantEvent(loadedEvent)) {
          navigate(`/unit/${unitId}/event/${eventId}/rsvp${location.search}`, { replace: true });
          return;
        }

        setEvent(loadedEvent);

        if (currentUser) {
          const rsvpsByUserIdQuery = query(
            collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
            where('userId', '==', currentUser.uid),
          );
          const rsvpsByUserIdSnapshot = await getDocs(rsvpsByUserIdQuery);
          const activeRsvpByUserId = findFirstActiveRsvpDoc(rsvpsByUserIdSnapshot.docs);
          if (activeRsvpByUserId && activeRsvpByUserId.data()?.status !== 'cancelled') {
            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${activeRsvpByUserId.id}/tickets-dashboard`, { replace: true });
            return;
          }

          if (currentUser.email) {
            const rsvpsByEmailQuery = query(
              collection(db, 'units', unitId, 'events', eventId, 'rsvps'),
              where('email', '==', currentUser.email.trim()),
            );
            const rsvpsByEmailSnapshot = await getDocs(rsvpsByEmailQuery);
            const activeRsvpByEmail = findFirstActiveRsvpDoc(rsvpsByEmailSnapshot.docs);
            if (activeRsvpByEmail && activeRsvpByEmail.data()?.status !== 'cancelled') {
              const rsvpData = activeRsvpByEmail.data();
              if (rsvpData && !rsvpData.userId) {
                try {
                  await updateDoc(activeRsvpByEmail.ref, { userId: currentUser.uid, updatedAt: new Date() });
                } catch (updateError) {
                  console.error('Error updating RSVP with userId:', updateError);
                }
              }
              navigate(`/unit/${unitId}/event/${eventId}/rsvp/${activeRsvpByEmail.id}/tickets-dashboard`, { replace: true });
              return;
            }
          }
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        showError('Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [unitId, eventId, currentUser, navigate, location.key, ticketsParam]);

  useEffect(() => {
    if (!currentUser) {
      setLockPersonalDetailsFromAccount(false);
      return;
    }
    // Wait for ranks to finish loading (not for a non-empty list) so rankId can be matched.
    if (ranksLoading) return;

    const fetchUserProfile = async () => {
      try {
        const nextEmail = currentUser.email || '';
        setEmail(nextEmail);

        let nextFirst = '';
        let nextLast = '';

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.firstName) {
            nextFirst = userData.firstName;
          } else if (userData.name) {
            nextFirst = userData.name.split(' ')[0] || '';
          }
          if (userData.lastName) {
            nextLast = userData.lastName;
          } else if (userData.name) {
            nextLast = userData.name.split(' ').slice(1).join(' ') || '';
          }

          if (userData.primaryUnits && Array.isArray(userData.primaryUnits) && userData.primaryUnits.length > 0) {
            const primaryUnit = userData.primaryUnits[0];
            if (primaryUnit.rankId) {
              const matchedRank = ranks.find((r) => r.id === primaryUnit.rankId);
              if (matchedRank) setRank(matchedRank.name);
            }
            if (primaryUnit.rank) setAdditionalRank(primaryUnit.rank);
            if (primaryUnit.unitName) setUnitName(primaryUnit.unitName);
          } else {
            if (userData.rank) setRank(userData.rank);
            if (userData.unitName) setUnitName(userData.unitName);
          }
        }

        if ((!nextFirst || !nextLast) && currentUser.displayName) {
          const parts = currentUser.displayName.trim().split(/\s+/);
          if (!nextFirst) nextFirst = parts[0] || '';
          if (!nextLast) nextLast = parts.slice(1).join(' ');
        }

        setFirstName(nextFirst);
        setLastName(nextLast);
        setLockPersonalDetailsFromAccount(nextFirst.trim() !== '' && nextLast.trim() !== '' && nextEmail.trim() !== '');
      } catch (error) {
        console.error('Error fetching user profile:', error);
        setLockPersonalDetailsFromAccount(false);
      }
    };

    fetchUserProfile();
  }, [currentUser, ranks, ranksLoading]);

  const checkEmailExists = async (emailToCheck: string): Promise<boolean> => {
    try {
      const methods = await fetchSignInMethodsForEmail(auth, emailToCheck);
      return methods.length > 0;
    } catch (error) {
      console.error('Error checking email:', error);
      return false;
    }
  };

  const handleEmailBlur = async () => {
    if (email && !currentUser) {
      const exists = await checkEmailExists(email);
      setEmailHasAccount(exists);
    }
  };

  const handleLogin = async () => {
    if (!loginPassword) {
      showError('Please enter your password');
      return;
    }
    try {
      await signInWithEmailAndPassword(auth, email, loginPassword);
      showSuccess('Logged in successfully!');
      setLoginPassword('');
      setEmailHasAccount(false);
    } catch (error: any) {
      console.error('Login error:', error);
      if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
        showError('Incorrect password. Please try again.');
      } else {
        showError('Failed to log in. Please try again.');
      }
    }
  };

  const setQuantity = (ticketId: string, qty: number) => {
    setQuantities((prev) => ({ ...prev, [ticketId]: Math.max(0, qty) }));
  };

  const cartTotal = useMemo(() => computeMultiTenantCartTotal(availableTickets, quantities), [availableTickets, quantities]);
  const totalQuantity = useMemo(() => computeMultiTenantTotalQuantity(quantities), [quantities]);
  const bookingFeePounds = useMemo(
    () => getInvoiceCreditRecoveryPounds(event, cartTotal, creditBookingFeePence),
    [event, cartTotal, creditBookingFeePence],
  );
  const showBookingFeeNote = event ? shouldShowBookingFeeOnRsvp(event, cartTotal, false, creditBookingFeePence) : false;

  const submitRsvp = async () => {
    if (submitting) return;

    if (!firstName.trim() || !lastName.trim() || !email.trim()) {
      showError('Please fill in all required fields');
      return;
    }

    const ticketValidationError = validateMultiTenantTicketSelections(availableTickets, quantities);
    if (ticketValidationError) {
      showError(ticketValidationError);
      return;
    }

    if (!event || !unitId || !eventId) {
      showError('Invalid event');
      return;
    }

    if (event.eventStatus === 'closed') {
      showError('This event is locked. New RSVPs are no longer being accepted.');
      return;
    }

    if (!currentUser && emailHasAccount) {
      showError('This email is already registered. Please log in to continue.');
      return;
    }

    if (requireAffiliationInfo) {
      if (!hasConfiguredPrefixes) {
        showError('This unit has no configured prefixes. Please contact the event organizer.');
        return;
      }
      if (!rank.trim()) {
        showError('Please select your prefix.');
        return;
      }
    }

    setSubmitting(true);
    try {
      const latestEventDoc = await getDoc(doc(db, 'units', unitId, 'events', eventId));
      if (!latestEventDoc.exists() || latestEventDoc.data()?.eventStatus === 'closed') {
        showError('This event was locked before your RSVP could be submitted.');
        return;
      }

      const userId = currentUser?.uid;
      const formattedFirstName = formatRsvpFirstName(firstName);
      const formattedLastName = formatRsvpLastName(lastName);

      const responses: Record<string, any> = { Allergies: allergies };
      mergeMultiTenantTicketsIntoResponses(responses, availableTickets, quantities);

      const rsvpData: any = {
        eventId,
        firstName: formattedFirstName,
        lastName: formattedLastName,
        name: `${formattedFirstName} ${formattedLastName}`,
        // Stored lower-case to match the email index key and sign-in emails.
        email: email.trim().toLowerCase(),
        responses,
        status: event.maxCapacity && event.currentAttendees + totalQuantity > event.maxCapacity ? 'waitlist' : 'confirmed',
        apologies: false,
        checkedIn: false,
        plusOneAllowed: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      Object.assign(rsvpData, optionalRsvpUserIdFields(userId));

      const { rank: resolvedRank, rankShortName } = getRankAndShortName({
        rankName: rank,
        currentUnitType,
        globalRanks: ranks,
      });
      if (resolvedRank) rsvpData.rank = resolvedRank;
      rsvpData.rankShortName = rankShortName !== null ? rankShortName : null;
      if (additionalRank) rsvpData.additionalRank = additionalRank;
      if (unitName) rsvpData.unit = unitName;
      if (ticketsParam) rsvpData.ticketFilter = ticketsParam;

      const normalizedEmail = email.trim().toLowerCase();
      const rsvpRef = doc(collection(db, 'units', unitId, 'events', eventId, 'rsvps'));
      const emailIndexRef = doc(db, 'units', unitId, 'events', eventId, 'rsvpEmailIndex', normalizedEmail);

      // A cancelled prior booking should not block re-registration. The email index isn't
      // client-readable (it would reveal who's booked), so the check runs server-side.
      const checkRsvpEmailStatus = httpsCallable<
        { unitId: string; eventId: string; email: string },
        { exists: boolean; id?: string; status?: string | null; active?: boolean }
      >(functions, 'checkRsvpEmailStatus');
      const { data: existing } = await checkRsvpEmailStatus({ unitId, eventId, email: normalizedEmail });
      if (existing.exists) {
        if (existing.status !== 'cancelled') {
          if (existing.id && existing.active) {
            showError('You have already registered for this event. Please check your email for your confirmation.');
            navigate(`/unit/${unitId}/event/${eventId}/rsvp/${existing.id}/tickets-dashboard`);
            return;
          }
          showError('You have already registered for this event. Please check your email for your confirmation.');
          return;
        }
      }

      // Guests (no account) manage their tickets with a capability token; only its SHA-256 hash is
      // stored on the RSVP. The raw token lives in the guest's link / session storage and is verified
      // server-side by the getGuestRsvp/manageGuestRsvp/cancelGuestRsvp callables.
      let guestManageToken: string | null = null;
      if (!userId) {
        guestManageToken = generateGuestRsvpToken();
        (rsvpData as any).manageTokenHash = await hashGuestRsvpToken(guestManageToken);
        // Transient only: read once by the confirmation-email trigger to build a working dashboard
        // link, then deleted. Without this, the emailed link has no token and is a dead end.
        (rsvpData as any).pendingGuestToken = guestManageToken;
      }

      // Create the invoice eagerly in the same batch (guest or authed) so the RSVP already carries its
      // invoiceId — no post-create update is required (which anonymous guests can no longer perform).
      const invoiceRef = doc(collection(db, 'units', unitId, 'events', eventId, 'invoices'));
      const invoiceNumber = await generateUnitInvoiceNumber(db, unitId);

      const ticketPrice = cartTotal;
      const recoveryEnabled = isCreditRecoveryEnabledForEvent(event);
      const recoveryAmountPence = recoveryEnabled ? creditBookingFeePence : 0;
      const recoveryAmount = recoveryAmountPence / 100;
      const actualRecoveryAmount = ticketPrice > 0 ? recoveryAmount : 0;
      const platformFeeTotal = actualRecoveryAmount * 1;
      const totalAmount = ticketPrice + platformFeeTotal;

      const invoiceData: Record<string, any> = {
        eventId,
        unitId,
        ownerUid: userId ?? '',
        totalAmount,
        amountPaid: 0,
        outstandingBalance: totalAmount,
        status: 'outstanding',
        rsvps: [rsvpRef.id],
        paymentHistory: [],
        paymentLink: buildInvoicePaymentLink(window.location.origin, invoiceRef.id),
        remindersSent: [],
        invoiceNumber,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      if (recoveryEnabled && ticketPrice > 0) {
        invoiceData.allInPriceBreakdown = { ticketPrice, creditCostRecovery: platformFeeTotal };
        invoiceData.accountingTotals = {
          totalLodgeTransfer: ticketPrice,
          totalPlatformProfit: 0,
          platformFeeTotal,
        };
      }

      (rsvpData as any).invoiceId = invoiceRef.id;

      const batch = writeBatch(db);
      batch.set(emailIndexRef, { email: normalizedEmail, rsvpId: rsvpRef.id, createdAt: new Date() });
      batch.set(rsvpRef, rsvpData);
      batch.set(invoiceRef, invoiceData);
      await batch.commit();

      // currentAttendees is recounted server-side (syncEventAttendeeCount trigger).

      if (userId) {
        const updates: any = {};
        if (rank) updates.rank = rank;
        if (unitName) updates.unitName = unitName;
        if (Object.keys(updates).length > 0) {
          await updateDoc(doc(db, 'users', userId), updates);
        }
      }

      showSuccess('Tickets reserved successfully!');

      if (!currentUser && guestManageToken) {
        // Guests carry their capability token so the tickets dashboard can load their booking via the
        // token-verified getGuestRsvp callable (RSVPs are no longer publicly readable).
        storeGuestRsvpToken(rsvpRef.id, guestManageToken);
      }

      if (totalAmount > 0) {
        // Multi-tenant bookings always have something owing (the whole point is buying tickets), so
        // send the purchaser straight to payment instead of the dashboard, unlike the standard flow.
        navigate(`/payment/${invoiceRef.id}`);
      } else {
        const dashboardPath = `/unit/${unitId}/event/${eventId}/rsvp/${rsvpRef.id}/tickets-dashboard`;
        navigate(
          !currentUser && guestManageToken
            ? `${dashboardPath}?t=${encodeURIComponent(guestManageToken)}`
            : dashboardPath,
        );
      }
    } catch (error) {
      console.error('Error submitting ticket purchase:', error);
      showError('Failed to submit your ticket purchase. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitRsvp();
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading event...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <Card className="max-w-md">
            <CardHeader>
              <CardTitle>Event Not Found</CardTitle>
              <CardDescription>The event you're looking for doesn't exist or has been removed.</CardDescription>
            </CardHeader>
            <CardContent>
              <Button onClick={() => navigate('/')}>Return Home</Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isEventLocked) {
    return (
      <div className="flex min-h-screen flex-col">
        <Header />
        <div className="flex flex-1 items-center justify-center px-4">
          <Card className="max-w-md w-full">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                <Lock className="h-8 w-8 text-amber-700" />
              </div>
              <h1 className="text-2xl font-semibold text-foreground">This Event is Locked</h1>
              <p className="mt-2 text-sm text-muted-foreground">This event is no longer accepting new ticket purchases.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <EventPageBackground backgroundImageUrl={event.backgroundImageUrl} backgroundColor={event.backgroundColor}>
      <Header />
      <main className="flex-1 px-3 py-8 sm:px-6">
        <div className="w-full sm:mx-auto sm:max-w-3xl">
          {event.bannerUrl && (
            <div className="mb-6 rounded-lg overflow-hidden">
              <img src={event.bannerUrl} alt="Event Banner" className="w-full h-auto object-cover" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
          )}

          <Card className="mb-6">
            <CardHeader>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  {(event.logoUrl || currentUnit?.settings?.logoUrl) && (
                    <div className="mb-4">
                      <img src={event.logoUrl || currentUnit?.settings?.logoUrl} alt="Event Logo" className="h-20 object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    </div>
                  )}
                  <CardTitle className="text-2xl">{event.name}</CardTitle>
                  {event.ceremonyType && <p className="text-sm text-primary font-medium mt-1">{event.ceremonyType}</p>}
                  <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      {formatDate(event.date)}
                    </div>
                    <div className="flex items-start text-base">
                      <MapPin className="mr-2 h-4 w-4 mt-0.5 flex-shrink-0" />
                      <VenueInfo unitId={unitId!} venueId={event.venueId} location={event.location} compact={true} />
                    </div>
                    <div className="flex items-center text-base">
                      <Calendar className="mr-2 h-4 w-4" />
                      RSVP deadline: {formatDate(event.rsvpDeadline)}
                    </div>
                  </div>
                </div>
                <Badge variant="default">{event.currentAttendees}/{event.maxCapacity || '∞'} attending</Badge>
              </div>
            </CardHeader>
          </Card>

          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Your Details</CardTitle>
                <CardDescription>You'll be the named purchaser for this group of tickets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {requireAffiliationInfo && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="rank">Prefix</Label>
                        <Select value={rank} onValueChange={(value) => setRank(value)}>
                          <SelectTrigger id="rank">
                            <SelectValue placeholder="Select your prefix" />
                          </SelectTrigger>
                          <SelectContent>
                            {availablePrefixes.map((prefix) => (
                              <SelectItem key={prefix.id} value={prefix.name}>
                                {prefix.shortName ? `${prefix.name} (${prefix.shortName})` : prefix.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="additionalRank">Rank</Label>
                        <Input id="additionalRank" type="text" placeholder="eg. ProvJGW / PPADoC / AGSec" value={additionalRank} onChange={(e) => setAdditionalRank(e.target.value)} />
                      </div>
                    </div>
                    {!hasConfiguredPrefixes && (
                      <p className="text-xs text-amber-600">No prefixes are configured for this unit type. Please contact the event organizer.</p>
                    )}
                  </div>
                )}

                {currentUser && lockPersonalDetailsFromAccount ? (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>First Name</Label>
                        <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">{firstName}</div>
                      </div>
                      <div className="space-y-2">
                        <Label>Last Name</Label>
                        <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">{lastName}</div>
                      </div>
                    </div>
                    {requireAffiliationInfo && (
                      <div className="space-y-2">
                        <Label htmlFor="unit">{unitLabel} <span className="font-normal text-muted-foreground">(optional)</span></Label>
                        <Input id="unit" type="text" placeholder={currentUnitType ? `${currentUnitType.entityName} name` : 'Veritas Unit No. 1234'} value={unitName} onChange={(e) => setUnitName(e.target.value)} />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label>Email Address</Label>
                      <div className="rounded-md border border-input bg-muted px-3 py-2 text-sm">{email}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input id="firstName" type="text" placeholder="John" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input id="lastName" type="text" placeholder="Smith" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
                      </div>
                    </div>
                    {requireAffiliationInfo && (
                      <div className="space-y-2">
                        <Label htmlFor="unit">{unitLabel} <span className="font-normal text-muted-foreground">(optional)</span></Label>
                        <Input id="unit" type="text" placeholder={currentUnitType ? `${currentUnitType.entityName} name` : 'Veritas Unit No. 1234'} value={unitName} onChange={(e) => setUnitName(e.target.value)} />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input id="email" type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} onBlur={handleEmailBlur} required />
                      {emailHasAccount && !currentUser && (
                        <p className="text-xs text-amber-600">This email is already registered. Please log in below to continue.</p>
                      )}
                    </div>
                    {emailHasAccount && !currentUser && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-2 rounded-full bg-amber-600" />
                          <p className="text-sm font-semibold text-amber-900">Login Required</p>
                        </div>
                        <p className="text-sm text-amber-800">This email address is associated with an existing account. Please log in to continue.</p>
                        <div className="space-y-2">
                          <Label htmlFor="loginPassword">Password</Label>
                          <Input
                            id="loginPassword"
                            type="password"
                            placeholder="Enter your password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleLogin();
                              }
                            }}
                          />
                        </div>
                        <Button type="button" onClick={handleLogin} className="w-full" variant="default">
                          Log In
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Select Your Tickets</CardTitle>
                <CardDescription>Choose how many of each ticket type you'd like to purchase</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {availableTickets.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No tickets are currently available for this event.</p>
                ) : (
                  availableTickets.map((ticket) => {
                    const qty = quantities[ticket.id] ?? 0;
                    const lineTotal = ticket.price * qty;
                    return (
                      <div key={ticket.id} className="rounded-lg border p-4">
                        <p className="font-medium break-words">{ticket.name}</p>
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                          <div className="min-w-0">
                            <p className="text-sm text-muted-foreground">{formatCurrency(ticket.price)} each</p>
                            {typeof ticket.totalAvailable === 'number' && (
                              <p className="text-xs text-muted-foreground">{ticket.totalAvailable} available</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setQuantity(ticket.id, qty - 1)} disabled={qty <= 0}>
                              <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                              type="number"
                              min={0}
                              max={ticket.totalAvailable ?? undefined}
                              value={qty}
                              onChange={(e) => setQuantity(ticket.id, parseInt(e.target.value, 10) || 0)}
                              className="w-14 shrink-0 px-1 text-center"
                            />
                            <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => setQuantity(ticket.id, qty + 1)}>
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                          <p className="ml-auto text-sm font-semibold">{formatCurrency(lineTotal)}</p>
                        </div>
                      </div>
                    );
                  })
                )}

                {availableTickets.length > 0 && (
                  <div className="flex items-center justify-between border-t pt-4">
                    <div>
                      <p className="font-semibold">Total ({totalQuantity} ticket{totalQuantity === 1 ? '' : 's'})</p>
                      {showBookingFeeNote && cartTotal > 0 && (
                        <p className="text-xs text-muted-foreground">Plus {BOOKING_FEE_LABEL}: {formatCurrency(bookingFeePounds)}</p>
                      )}
                    </div>
                    <p className="text-xl font-bold">{formatCurrency(cartTotal + (showBookingFeeNote && cartTotal > 0 ? bookingFeePounds : 0))}</p>
                  </div>
                )}

                <div className="space-y-2 border-t pt-4">
                  <Label htmlFor="allergies">Dietary Requirements</Label>
                  <Textarea
                    id="allergies"
                    placeholder="Please list any allergies or special dietary requirements..."
                    rows={3}
                    value={allergies}
                    onChange={(e) => setAllergies(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    This information will be shared with the kitchen staff to ensure your safety
                  </p>
                </div>
              </CardContent>
            </Card>

            <Button type="submit" className="w-full" disabled={submitting || isEventLocked}>
              {submitting ? 'Reserving Tickets...' : 'Reserve Tickets'}
            </Button>
          </form>
        </div>
      </main>
    </EventPageBackground>
  );
}
